import { inject, provide, type InjectionKey } from 'vue';

/**
 * Business apps keep the browser's address autofill out of their fields.
 *
 * Chromium ignores `autocomplete="off"` for addresses: it reads "Adresse / PLZ /
 * Ort" off the labels, counts every successful save request of a record mask
 * as a form submission and then asks "Adresse speichern / aktualisieren?" —
 * after every save (I-2236). Neither an unknown autocomplete token nor `off` on
 * the form stops that import; an input of type `search` is not parsed at all
 * (both checked in chrome://autofill-internals). So text fields inside such a
 * scope render as `type="search"` with the text-box role (a screen reader keeps
 * saying "Eingabefeld", not "Suchfeld") and the look of a text field, plus the
 * flags password-manager extensions honour (1Password, LastPass, Bitwarden,
 * Dashlane). A field that asks for autofill with a real token
 * (`autocomplete="username"`, `"email"`, …) opts back in and keeps its type;
 * `autocomplete="off"` is no opt-in.
 *
 * Every plain text input in the scope has to switch: one `type="text"` field
 * left in the page (Chromium groups all form-less fields into one form) makes
 * Chromium parse the `search` fields as addresses again.
 *
 * The drop-downs need two more things in Chromium (both photographed in a
 * real Chrome with a saved address and form history):
 * - `off` is ignored for address suggestions: "Adresse" still offered the
 *   saved "Privatweg 7". An autocomplete token Chromium does not know turns
 *   filling off for the field (`HTML_TYPE_UNRECOGNIZED (disabling autofill)`).
 * - That token is no `off`, so Chromium's single-field history comes back
 *   ("Anfrage" offered "Anfrage 4711"). History is keyed by the field name;
 *   a name unique to this page load never matches an old entry.
 * Firefox and Safari honour `off` for both and would read the token as "on",
 * so they keep `off` and only get the unique name.
 */
const browserAutofillOffInjectionKey = Symbol('nxBrowserAutofillOff') as InjectionKey<boolean>;

/** Call in an app's root component: every kit text field below stays out of browser autofill. */
function provideBrowserAutofillOff(): void {
  provide(browserAutofillOffInjectionKey, true);
  installSearchEscapeGuard();
}

/**
 * A `search` field must behave like a text field — but Chromium clears a
 * `search` input on Escape (its default action of the keydown): it fires
 * `input` with the empty value, then `search`, and `change` when the field is
 * left. A lazy model commits the empty value then, and a table cell saved an
 * empty line title after the user only pressed Escape (I-2510). preventDefault
 * on the keydown would stop the clear, but every Escape handler that respects
 * `defaultPrevented` (modals, popovers, the table's cell navigation) would
 * then ignore the key.
 *
 * So the guard leaves the key alone and undoes the clear instead, before
 * anyone sees it: on a window capture listener — the first stop of every
 * event, also when a dropdown stops Escape in the document's capture phase —
 * an Escape on one of the kit's search-typed text fields (`data-form-type`
 * `other`, see BROWSER_AUTOFILL_OFF_ATTRS) arms it with the field's value; the
 * clear's `input` gets the value back and stops there, its `search` stops
 * too, and no `change` follows, since the value never changed. Real search
 * fields (no marker) keep clearing on Escape. Installed once per window.
 */
let searchEscapeGuardInstalled = false;
let armed: { input: HTMLInputElement; value: string } | null = null;

function isKitSearchField(field: EventTarget | null): field is HTMLInputElement {
  return (
    field instanceof HTMLInputElement &&
    field.type === 'search' &&
    field.dataset.formType === BROWSER_AUTOFILL_OFF_ATTRS['data-form-type']
  );
}

/**
 * The field's own Escape handling put an older value back (NxInput's restore,
 * after the capture listener armed the guard with the typed one): the clear that
 * follows must give back the restored value, not the discarded typing. Also arms
 * a field that was empty at the keydown, since the restored value is not.
 */
function rearmSearchEscapeGuard(input: HTMLInputElement): void {
  if (!searchEscapeGuardInstalled || !isKitSearchField(input)) return;
  armed = input.value !== '' ? { input, value: input.value } : null;
}

function installSearchEscapeGuard(target: Window | undefined = globalThis.window): void {
  if (searchEscapeGuardInstalled || !target) return;
  searchEscapeGuardInstalled = true;
  target.addEventListener(
    'keydown',
    (event) => {
      const field = event.target;
      armed =
        event.key === 'Escape' && isKitSearchField(field) && field.value !== ''
          ? { input: field, value: field.value }
          : null;
    },
    true
  );
  // A key nobody's clear followed (a handler prevented it) disarms on release.
  target.addEventListener('keyup', () => (armed = null), true);
  const undoClear = (event: Event) => {
    if (!armed || event.target !== armed.input) return;
    if (event.type === 'input') {
      // Something else changed the value: not the clear — let it through.
      if (armed.input.value !== '') {
        armed = null;
        return;
      }
      armed.input.value = armed.value;
    } else {
      armed = null;
    }
    event.stopImmediatePropagation();
  };
  target.addEventListener('input', undoClear, true);
  target.addEventListener('search', undoClear, true);
}

const BROWSER_AUTOFILL_OFF_ATTRS = {
  type: 'search',
  role: 'textbox',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const;

/** A token Chromium does not recognise: no address suggestions for the field. */
const CHROMIUM_NO_AUTOFILL = 'nx-no-autofill';

function isChromium(): boolean {
  return (
    typeof navigator !== 'undefined' && /\b(?:Chrome|Chromium|Edg)\//.test(navigator.userAgent)
  );
}

/** Differs on every page load, so no field name ever meets its old history. */
const PAGE_LOAD_NONCE = Math.random().toString(36).slice(2, 8);
let fieldCounter = 0;

type BrowserAutofillOffAttrs = typeof BROWSER_AUTOFILL_OFF_ATTRS & {
  autocomplete: string;
  name: string;
};

/**
 * For kit inputs: the attributes that keep a text field out of browser
 * autofill, or none when the app did not ask for it, the field is no plain
 * text field, or the caller asked for autofill with a real token.
 */
function useBrowserAutofillOffAttrs(): (
  type: string | undefined,
  callerAutocomplete: unknown
) => Partial<BrowserAutofillOffAttrs> {
  const off = inject(browserAutofillOffInjectionKey, false);
  if (!off) return () => ({});
  // One name per field instance, stable across re-renders.
  const attrs: BrowserAutofillOffAttrs = {
    ...BROWSER_AUTOFILL_OFF_ATTRS,
    autocomplete: isChromium() ? CHROMIUM_NO_AUTOFILL : 'off',
    name: `nx-${PAGE_LOAD_NONCE}-${++fieldCounter}`,
  };
  return (type, callerAutocomplete) => {
    const plainText = type === undefined || type === 'text';
    const wantsAutofill = typeof callerAutocomplete === 'string' && callerAutocomplete !== 'off';
    return plainText && !wantsAutofill ? attrs : {};
  };
}

export {
  installSearchEscapeGuard,
  provideBrowserAutofillOff,
  rearmSearchEscapeGuard,
  useBrowserAutofillOffAttrs,
};
